import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as sharedActions } from "@shared/store";
import { selectors as hospitalSelectors } from "@containers/Hospital/store";
import { selectors, actions } from "@containers/SensorDataSession/store";
import { Loader } from "@shared/components";
import { SensorDataSessionForm } from "@containers/SensorDataSession/components";
import { SensorDataSession } from "@shared/models";
import { MODAL_TYPES } from "@shared/constants";
import { useLoader } from "@shared/hooks";

import "./index.scss";
interface SensorDataSessionCreateContainerProps {
  session?: SensorDataSession;
  onClose: () => void;
}

const SensorDataSessionCreateContainer = (props: SensorDataSessionCreateContainerProps) => {
  const { onClose, session } = props;

  const dispatch = useDispatch();

  const createUpdateLoadingInfo = useLoader({
    name: "SensorDataSessionCreate",
    actionTypes: useMemo(
      () => [session ? actions.updateSensorDataSession : actions.createSensorDataSession],
      [session],
    ),
  });

  const uploadingFilesPercentage = useSelector(selectors.getFilesUploadingPercentage());
  const currentSessionDots = useSelector(selectors.getCurrentSessionDots());
  const hospital = useSelector(hospitalSelectors.getHospital());

  const loadingText = useMemo(() => {
    const uploading = uploadingFilesPercentage !== null ? uploadingFilesPercentage : undefined;
    return uploading === 100 ? "Analyzing..." : `Uploading...${uploading}%`;
  }, [uploadingFilesPercentage]);

  const createSession = useCallback(
    (data: FormData) => {
      dispatch(
        actions.createSensorDataSession.request({
          data,
          cb: (percentage: number) => {
            dispatch(actions.setFilesPercentage(percentage));
          },
        }),
      );
    },
    [dispatch],
  );

  const updateSession = useCallback(
    (data: FormData) => {
      if (session) {
        dispatch(
          actions.updateSensorDataSession.request({
            id: session.id,
            data,
            cb: (percentage: number) => {
              dispatch(actions.setFilesPercentage(percentage));
            },
          }),
        );
      }
    },
    [dispatch, session],
  );

  const changeFormValues = useCallback(() => {
    dispatch(sharedActions.updateOverlay({ leaveConfirm: true }));
  }, [dispatch]);

  const deleteSession = useCallback(
    (id: number) => {
      if (!session) return;
      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: "Delete",
            content: `Are you sure you want to delete Session ${session.name}?`,
            cancelBtnText: "Cancel",
            successBtnText: "Delete",
            onSuccess: () => {
              dispatch(
                actions.deleteSensorDataSession.request({
                  id,
                }),
              );

              onClose();
            },
          },
        }),
      );
    },
    [dispatch, onClose, session],
  );

  return (
    <div className="sensor-data-session-container">
      {hospital ? (
        <SensorDataSessionForm
          onChangeForm={changeFormValues}
          onCloseForm={onClose}
          onCreateForm={createSession}
          onUpdateForm={updateSession}
          session={session}
          savedDots={currentSessionDots}
          onDelete={deleteSession}
          hospital={hospital}
          loading={createUpdateLoadingInfo.isLoading}
        />
      ) : null}
      {createUpdateLoadingInfo.isLoading ? <Loader text={loadingText} className="session-loading" /> : null}
    </div>
  );
};

export default SensorDataSessionCreateContainer;
